import {
    AfterViewInit,
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {BehaviorSubject, debounceTime, fromEvent} from 'rxjs';
import {HeatmapClickPosition} from '../../_types/polls';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {areArraysEqualDeep} from '../../_utils/utils';

export interface HeatMapData {
    x: number,
    y: number,
    intensity: number,
}

@Component({
    selector: 'app-heatmap',
    templateUrl: './heatmap.component.html',
    styleUrls: ['./heatmap.component.scss'],
    exportAs: 'heatmap'
})
export class HeatmapComponent implements AfterViewInit, OnChanges {
    @ViewChild('hostElement') hostElement!: ElementRef;
    @ViewChild('heatmapCanvas', {static: true}) heatmapCanvas!: ElementRef<HTMLCanvasElement>;
    @ViewChild('imageRef', {static: true}) imageRef!: ElementRef<HTMLImageElement>;

    @Input({required: true}) img!: string;
    @Input() strictPoints: boolean = false; // Flag to toggle strict color mode
    @Input() strictColor: string = 'rgba(255, 0, 0, 0.8)'; // Default strict color as semi-transparent red
    @Input() maxClickRadius: number = 50;
    @Input() doubleMaxClickRadius: boolean = false;
    @Input() maxIntensity: number = 10;  // Control the maximum intensity for a single point
    @Input() clickData: HeatMapData[] = [];
    @Input() singleClick: boolean = false;
    @Input() disableVotes: boolean = false;
    @Input() maxHeight: boolean = false;
    @Input() autoMaxIntensity: boolean = false;

    @Output() onClick: EventEmitter<HeatmapClickPosition> = new EventEmitter<HeatmapClickPosition>();

    public isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    private context!: CanvasRenderingContext2D;
    private initialClickData: HeatMapData[] = [];
    private _clickData: HeatMapData[] = [];

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    ngAfterViewInit(): void {
        this.initialClickData = JSON.parse(JSON.stringify(this._clickData));
        this.adjustImageToHostWidth(this.img);

        fromEvent(window, 'resize').pipe(
            debounceTime(100),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => {
            this.adjustImageToHostWidth(this.img, true)
        });
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (changes['clickData']) {
            const newClickData = changes['clickData'].currentValue;
            if (!areArraysEqualDeep(changes['clickData'].previousValue, newClickData) || changes['clickData'].firstChange) {
                this._clickData = newClickData;
                if (this.context) {
                    this.renderHeatmap();
                }
            }
        }

        if (changes['disableVotes']) {
            this.disableVotes = changes['disableVotes'].currentValue;
        }
    }

    public resetHeatmapData(): void {
        this._clickData = JSON.parse(JSON.stringify(this.initialClickData));
        this.renderHeatmap();
    }

    private initCanvasInteraction(): void {
        const canvas = this.heatmapCanvas.nativeElement;

        // Add click listener to track clicks
        canvas.addEventListener('click', (event: MouseEvent): void => {
            const rect = canvas.getBoundingClientRect();
            const x = Number(((event.clientX - rect.left) / rect.width).toFixed(6));
            const y = Number(((event.clientY - rect.top) / rect.height).toFixed(6));

            if (!this.singleClick) {
                // Check if the point already exists in the clickData array
                const existingPoint = this._clickData.find(point => this.isPointNearby(point, x, y));

                if (existingPoint) {
                    // Increase the intensity of the existing point
                    existingPoint.intensity = Math.min(existingPoint.intensity + 1, this.maxIntensity);
                } else {
                    // Add a new heatmap point with initial intensity
                    this._clickData.push({x, y, intensity: 1});
                }
            } else {
                this._clickData = [{x, y, intensity: 1}];
                this.onClick.emit({
                    coordinate_y: y.toString(),
                    coordinate_x: x.toString()
                })
            }

            // Redraw the image and heatmap points
            this.renderHeatmap();
        });
    }

    private isPointNearby(point: { x: number, y: number }, x: number, y: number): boolean {
        const distance: number = Math.sqrt(Math.pow(point.x - x, 2) + Math.pow(point.y - y, 2));
        return distance < this.maxClickRadius / 2;  // Consider points nearby within half the max radius
    }

    private renderHeatmap(): void {
        const canvas: HTMLCanvasElement = this.heatmapCanvas.nativeElement;
        const ctx: CanvasRenderingContext2D = this.context;
        const rect: DOMRect = canvas.getBoundingClientRect();

        // Clear the canvas and redraw the base image
        // ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(this.imageRef.nativeElement, 0, 0, canvas.width, canvas.height);

        if (this.autoMaxIntensity) {
            this.maxIntensity = Math.max(...this._clickData.map(data => data.intensity));
        }

        // Loop through clicks and render heatmap circles
        this._clickData.forEach(click => {
            const x: number = click.x * rect.width;
            const y: number = click.y * rect.height;
            ctx.beginPath();
            ctx.arc(x, y, this.maxClickRadius, 0, Math.PI * 2);

            if (this.strictPoints) {
                // Use strict color if the flag is true
                ctx.fillStyle = this.strictColor;
            } else {
                // Create a color gradient based on the intensity
                const gradient: CanvasGradient = ctx.createRadialGradient(x, y, 0, x, y, this.maxClickRadius);
                const intensityColor: string = this.getColorForIntensity(click.intensity);
                gradient.addColorStop(0, intensityColor);
                gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

                ctx.fillStyle = gradient;
            }

            ctx.fill();
        });
    }

    private getColorForIntensity(intensity: number): string {
        const ratio: number = intensity / this.maxIntensity;
        let r: number = 0, g: number = 0, b: number = 0;

        if (ratio < 0.25) {
            // Transition from blue to cyan
            r = 0;
            g = Math.floor(4 * 255 * ratio);
            b = 255;
        } else if (ratio < 0.5) {
            // Transition from cyan to green
            r = 0;
            g = 255;
            b = Math.floor(255 - 4 * 255 * (ratio - 0.25));
        } else if (ratio < 0.75) {
            // Transition from green to yellow
            r = Math.floor(4 * 255 * (ratio - 0.5));
            g = 255;
            b = 0;
        } else {
            // Transition from yellow to red
            r = 255;
            g = Math.floor(255 - 4 * 255 * (ratio - 0.75));
            b = 0;
        }

        return `rgba(${r}, ${g}, ${b}, 0.9)`;  // Adjust alpha for transparency
    }

    private resizeImageToFitContainer(imageUrl: string, containerWidth: number, containerHeight?: number): Promise<{
        newWidth: number,
        newHeight: number
    }> {
        return new Promise((resolve, reject): void => {
            const img = new Image();
            img.onload = (): void => {
                let newWidth, newHeight;
                const aspectRatio: number = img.width / img.height;

                if (containerHeight) {
                    // If both width and height are provided, resize to fit the container while maintaining the aspect ratio
                    if (containerWidth / containerHeight > aspectRatio) {
                        // Height is the limiting factor
                        newHeight = containerHeight;
                        newWidth = containerHeight * aspectRatio;
                    } else {
                        // Width is the limiting factor
                        newWidth = containerWidth;
                        newHeight = containerWidth / aspectRatio;
                    }
                } else {
                    // If only width is provided, resize based on width while maintaining the aspect ratio
                    newWidth = containerWidth;
                    newHeight = containerWidth / aspectRatio;
                }

                this.maxClickRadius = Math.max(newWidth, newHeight) / (this.doubleMaxClickRadius ? 25 : 50);

                resolve({newWidth, newHeight});
            };

            img.onerror = (): void => reject(new Error('Error loading image'));
            img.src = imageUrl;
        });
    }


    private async adjustImageToHostWidth(imageUrl: string, skipAddCanvasInteraction: boolean = false): Promise<void> {
        const containerWidth = this.hostElement.nativeElement.clientWidth;
        const containerHeight = this.hostElement.nativeElement.clientHeight;
        try {
            const {
                newWidth,
                newHeight
            } = await this.resizeImageToFitContainer(imageUrl, containerWidth, this.maxHeight ? containerHeight : null);

            const canvas: HTMLCanvasElement = this.heatmapCanvas.nativeElement;
            canvas.width = newWidth;
            canvas.height = newHeight;


            this.context = canvas.getContext('2d')!;
            if (this.context) {
                const img = new Image();
                img.onload = (): void => {
                    this.context.drawImage(img, 0, 0, newWidth, newHeight);
                    if (!this.disableVotes && !skipAddCanvasInteraction) {
                        this.initCanvasInteraction();
                    } else {
                        this.renderHeatmap();
                    }
                    this.isLoading.next(false);
                };
                img.src = imageUrl;
            }
        } catch (error) {
            console.error(error);
        }
    }
}
